import type { AnswersByQuestionId, QuestionRoutingRules } from '@/modules/routing'
import { compressRoutingVisitStack } from './survey-routing-progress'
import type { SurveyDefinition } from './survey-question.types'
import { findSurveyQuestion } from './parse-survey-definition'

export function resolveCurrentQuestion(
  definition: SurveyDefinition,
  currentQuestionId: string | null,
) {
  return findSurveyQuestion(definition, currentQuestionId)
}

/** Previous question in visit history (Back navigation). */
export function previousQuestionId(visitedQuestionIds: readonly string[]): string | null {
  if (visitedQuestionIds.length < 2) return null
  return visitedQuestionIds.at(-2) ?? null
}

export function appendVisitedQuestion(
  visited: readonly string[],
  questionId: string,
): string[] {
  if (visited.at(-1) === questionId) {
    return [...visited]
  }
  return [...visited, questionId]
}

export function trimVisitedForBack(visited: readonly string[]): string[] {
  if (visited.length <= 1) return [...visited]
  return visited.slice(0, -1)
}

/**
 * Seeds Back navigation from server `routingPath` when resuming (TC-SPW-45/49).
 * Falls back to current question only when path is empty.
 */
export function initialVisitedQuestionIds(
  routingPath: string[] | undefined,
  currentQuestionId: string | null,
  fallbackFirstQuestionId: string | null,
): string[] {
  if (routingPath && routingPath.length > 0) {
    const path = [...routingPath]
    if (currentQuestionId && !path.includes(currentQuestionId)) {
      path.push(currentQuestionId)
    }
    return path
  }
  if (currentQuestionId) {
    return [currentQuestionId]
  }
  if (fallbackFirstQuestionId) {
    return [fallbackFirstQuestionId]
  }
  return []
}

/**
 * After save, extend the visit stack and collapse segments only when saved answers
 * confirm a routing jump skipped intermediates (not on linear continue paths).
 */
export function visitedIdsAfterAdvance(
  previousVisited: readonly string[],
  nextQuestionId: string,
  definition: SurveyDefinition,
  routingRules?: QuestionRoutingRules[],
  answersByQuestionId?: AnswersByQuestionId,
): string[] {
  const appended = appendVisitedQuestion(previousVisited, nextQuestionId)
  if (routingRules?.length) {
    return compressRoutingVisitStack(
      appended,
      routingRules,
      definition,
      answersByQuestionId,
    )
  }
  return appended
}

export { countAnsweredInDefinition as countAnsweredQuestions } from './survey-question-layout'
